import { unwrap } from "@sagot/api-client";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../../build/shared/keys";
import { useAgentList } from "../shared/use-engage-data";

const REFERENCE_STALE_MS = 60_000;

/** An action a booking card or lead form can show (actions belong to an agent). */
export interface ActionOption {
  id: string;
  name: string;
  kind: string;
  agentId: string;
  agentName: string;
}

/** Every agent's actions (shares the Actions page's cache); empty while the member may not read them. */
export function useWorkflowActions(enabled: boolean): { actions: ActionOption[]; loading: boolean } {
  const api = useApi();
  const ws = useWorkspaceId();
  const agents = useAgentList();
  const agentList = useMemo(() => agents.data ?? [], [agents.data]);
  const lists = useQueries({
    queries: agentList.map((agent) => ({
      queryKey: buildKeys.actions(ws, agent.id),
      queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/actions", { params: { path: { agent_id: agent.id } } })),
      enabled,
      retry: false,
      staleTime: REFERENCE_STALE_MS,
    })),
    combine: (results) => ({ data: results.map((result) => result.data), loading: results.some((result) => result.isLoading) }),
  });
  const actions = useMemo(
    () => agentList.flatMap((agent, index) => (lists.data[index] ?? []).map((action) => ({ id: action.id, name: action.name, kind: action.kind, agentId: agent.id, agentName: agent.name }))),
    [agentList, lists.data],
  );
  return { actions, loading: lists.loading || agents.isLoading };
}
